import cookies from 'js-cookie';
import { camelCase, get, isEmpty, pick, flatten, isObject, uniqBy, upperFirst } from 'lodash';
import { attendees as attendeesUtils } from '@clubmed/utils';
import moment from 'moment';
import * as yup from 'yup';
import 'formik';
import { redirectToUri } from 'Utils/uri/redirectUtils';
import { getAdultsCivilities, getKidsCivilities } from '@clubmed/domains-service';
import graphQLClient from 'Utils/graphql-client/graphqlClient';
import {
  PAYMENT_METHOD_LABEL,
  PAYMENT_METHOD_CATEGORY,
} from 'Components/widgets/payment-info/PaymentInfo.const';
import { buildUrl } from 'Routes/routeBuilder';
import {
  STATUS_CODE_200,
  STATUS_CODE_400,
  STATUS_CODE_401,
  STATUS_CODE_500,
} from 'Utils/status-code/statusCode.constants';
import { PROPOSAL_UPDATE } from 'Utils/request/requestDictionary';
import type {
  AdultAttendeeWithInfosType,
  ChildAttendeeWithInfosType,
} from 'Store/booking/booking.selector';
import 'Store/booking/booking.selector';
import type { State as UserInfoType } from 'Store/user-info/reducers/userInfoReducer';
import 'Store/user-info/reducers/userInfoReducer';
import { TRACK_EVENT_NAMES } from 'Tracking/constants';
import {
  EMAIL_OPTIN,
  FOSUN_PARTNERS,
  MAIL_OPTIN,
  SMS_OPTIN,
  PHONE_OPTIN,
  CLUBMED_PLUS,
  DOCUMENT_TYPES,
  PASSPORT,
  IDCARD,
} from 'Utils/legal-information/legalInformationIds.constants';
import {
  LEGAL_INFO_THEO_ID,
  LEGAL_INFO_WEBTHEO_ID,
  LEGAL_INFO_NDC_ID,
} from 'Utils/booking-process/constants';
import { apiDateToTimestamp, timestampToApiDate } from 'Utils/date/apiDate';
import { emailRegex, getPhoneRegex, salesmanIDRegex } from 'Utils/regex/RegexHelper';
import { t } from 'Src/i18n';
import { BOOKING_ALREADY_EXISTS } from 'Utils/option-validity/constants';
import type { MemberHousehold } from 'Views/booking/receipt/home-details/HomeDetailsWrapper.utils';
import 'Views/booking/receipt/home-details/HomeDetailsWrapper.utils';
import type { AttendeesType } from 'Store/booking/attendees/attendees.selector';
import 'Store/booking/attendees/attendees.selector';
import { CNIL_LEGAL_MESSAGE_ID, MAX_AGE, YEAR_OF_MAJORITY, FORM_ERROR_OBJECT } from './constants';
import { identityNumberValidatorReducer } from './contact-info-form/identityNumberValidator.reducer';
// removing mergeSchemas method cause it does not have expected behavior.
// https://github.com/jquense/yup#schemaconcatschema-schema-schema -> doc explaning that is does not merge content but override previous schema.
// https://github.com/jquense/yup/issues/1535 -> issue explaining that it does not merge content but override previous schema.
// Using yup.addMethods that allows us to add custom methods to Yup when needed so it's possible to chain schemas in prefered order.
export type LegalType = {
  id: string;
  label: string;
  description: string;
  required: boolean;
  concernedAttendees: string | null | undefined;
};
type FieldsOnErrorType = Record<string, string | FieldsOnErrorType>;

function getCNILLegalMessage(
  legalMessages: ReadonlyArray<{
    id: string;
    label: string;
    description: string;
  }> = [],
):
  | {
      label: string;
      description: string;
      required?: boolean;
    }
  | null
  | undefined {
  const cnilLegalMessage = legalMessages.find(
    (legalMessage) => legalMessage.id === CNIL_LEGAL_MESSAGE_ID,
  );
  return cnilLegalMessage && pick(cnilLegalMessage, ['label', 'description', 'required']);
}

function getLegalInputInformation(
  legalMessages: ReadonlyArray<LegalInformation> = [],
  id: string,
  type?: string,
) {
  return legalMessages.find(
    (legalMessage) =>
      legalMessage.id === id && (type && legalMessage.type ? legalMessage.type === type : true),
  );
}

function getLegalInformation(
  beLegalInfos: ReadonlyArray<LegalInformation> | null | undefined,
): (arg0: string) => LegalInformation | null | undefined {
  return (id) => (beLegalInfos || []).find((legalInfo) => legalInfo.id === id);
}

export const getOptinsInfos = (bookingProcess: I18N['bookingProcess']) => {
  const getLegalInfo = getLegalInformation(bookingProcess);
  return {
    email: getLegalInfo(EMAIL_OPTIN),
    sms: getLegalInfo(SMS_OPTIN),
    mail: getLegalInfo(MAIL_OPTIN),
    fosun: getLegalInfo(FOSUN_PARTNERS),
    phone: getLegalInfo(PHONE_OPTIN),
    clubmedPlus: getLegalInfo(CLUBMED_PLUS),
    theo: getLegalInfo(LEGAL_INFO_THEO_ID),
    webTheo: getLegalInfo(LEGAL_INFO_WEBTHEO_ID),
    ndc: getLegalInfo(LEGAL_INFO_NDC_ID),
  };
};

function shouldDisplayLegalInformation(
  beLegalInfos: ReadonlyArray<LegalInformation>,
): (arg0: string) => boolean {
  const getLegalInfo = getLegalInformation(beLegalInfos);
  return (id) => !!getLegalInfo(id);
}

function _getOptionSubmissionRequest(params, hasSaveOption, isChequeOrTransferPaymentLabel) {
  return `
    mutation {
      bookings(
        proposalId: "${params.proposalId}"
        ${hasSaveOption || isChequeOrTransferPaymentLabel ? 'bookingStatus: OPTION' : 'bookingStatus: PENDING_PAYMENT'}
      ) {
        bookingId
        expirationDateTime
        isReliable
      }
    }`;
}

type optionFromProposal = {
  appContext: AppContext;
  hasNoRedirection?: boolean;
  hasSaveOption?: boolean;
  productId?: any;
  router: any;
  salesmanID?: string;
  providerId?: string;
  selectedProposalId: string;
  sellerEmail?: string;
  withfutureTravelCredits?: boolean;
};

function submitOptionFromProposal({
  appContext,
  hasNoRedirection,
  hasSaveOption,
  productId,
  router,
  selectedProposalId,
  providerId,
  withfutureTravelCredits,
}: optionFromProposal) {
  return () => {
    const redirectTo = redirectToUri(router);
    const params = {
      proposalId: selectedProposalId,
    };
    const paymentMethodLabel = cookies.get(PAYMENT_METHOD_LABEL);
    const isChequeOrTransferPaymentLabel = [
      PAYMENT_METHOD_CATEGORY.CHEQUE,
      PAYMENT_METHOD_CATEGORY.BANK_TRANSFER,
    ].includes(upperFirst(camelCase(paymentMethodLabel)));

    const query = _getOptionSubmissionRequest(
      params,
      hasSaveOption,
      isChequeOrTransferPaymentLabel,
    );

    const handleSuccess = (response) => {
      const queryParams: {
        bi: string;
        expirationTime?: string;
        isReliable?: boolean;
        selectedProposalId: string;
        productId?: any;
        providerId?: string;
        hasSaveOption?: boolean;
        futureTravelCreditOption?: boolean;
      } = {
        bi: response?.bookings?.bookingId || '',
        expirationTime: response?.bookings?.expirationDateTime || '',
        isReliable: response?.bookings?.isReliable,
        selectedProposalId,
        providerId,
        productId,
        ...(hasSaveOption && {
          hasSaveOption,
        }),
      };

      if (withfutureTravelCredits) {
        queryParams.futureTravelCreditOption = true;
      }

      !hasNoRedirection &&
        redirectTo(
          buildUrl(
            'optionConfirmation',
            {
              status: STATUS_CODE_200,
            },
            queryParams,
          ),
        )();
      return {
        isRedirectionPerfomed: false,
        reason: null,
        bookingId: response?.bookings?.bookingId,
      };
    };

    const handleError = (error) => {
      const status = get(error, 'errors[0].statusCode', STATUS_CODE_400);
      const errorCode = get(error, 'errors[0].errorCode', 'BOOKING_UNAVAILABLE');
      const isBookingInSameTimeOfProposalError =
        hasSaveOption && errorCode === BOOKING_ALREADY_EXISTS;
      !hasNoRedirection &&
        !isBookingInSameTimeOfProposalError &&
        redirectTo(
          buildUrl(
            'optionConfirmationError',
            {
              status,
              error_code: errorCode,
            },
            {
              selectedProposalId,
              productId,
            },
          ),
        )();
      return {
        isRedirectionPerfomed: false,
        reason: isBookingInSameTimeOfProposalError ? BOOKING_ALREADY_EXISTS : null,
        bookingId: null,
        response: null,
      };
    };

    return graphQLClient
      .runNoCacheQuery(query, appContext, {
        requestName: PROPOSAL_UPDATE,
      })
      .then(handleSuccess)
      .catch(handleError);
  };
}

function submitOption({
  appContext,
  router,
  hasSaveOption,
  withfutureTravelCredits,
}: {
  appContext: AppContext;
  router: any;
  hasSaveOption?: boolean;
  withfutureTravelCredits?: boolean;
}): (arg0: any) => Promise<any> {
  const { selectedProposalId, product: productId } = router.query;
  return submitOptionFromProposal({
    appContext,
    selectedProposalId,
    productId,
    hasSaveOption,
    router,
    withfutureTravelCredits,
  });
}

function renderErrorUpdatingAttendees({
  errors,
  statusCode,
}: {
  errors: ErrorType;
  statusCode?: string;
}): string {
  if (statusCode && (statusCode === STATUS_CODE_500 || statusCode === STATUS_CODE_401)) {
    return t('booking.form.errors.technicalErrorParticipants');
  }

  if (errors?.length) {
    const errorCode = errors?.[0]?.errorCode;
    const cmsErrorKey = `booking.participants.errorMessage.${errorCode}`;

    if (errorCode && cmsErrorKey !== t(cmsErrorKey)) {
      return t(cmsErrorKey);
    }
  }

  return t('booking.form.errorLabel');
}

function getConcernedAttendees({ errors }: { errors: ErrorType }) {
  return errors?.[0]?.errorCode === 'invalidPassportExpirationDate'
    ? errors?.[0]?.concernedAttendees
    : [];
}

const getAttendeeId = ({ neolid }) => neolid;

const getAllAttendeesIds = ({
  adults,
  kids,
}: TripInformation): {
  adultsAttendeesIds?: Array<string>;
  kidsAttendeesIds?: Array<string>;
} => {
  const allAttendeesIds = {};

  if (adults.length > 0) {
    allAttendeesIds.adultsAttendeesIds = adults.map(getAttendeeId).filter(Boolean);
  }

  if (kids && kids.length > 0) {
    allAttendeesIds.kidsAttendeesIds = kids.map(getAttendeeId).filter(Boolean);
  }

  return allAttendeesIds;
};

export type AdultInitialAttendeesListType = AdultAttendeeWithInfosType & {
  isMain?: boolean;
};
export type InitialAttendeesListType = {
  adults: ReadonlyArray<AdultInitialAttendeesListType>;
  children: ReadonlyArray<ChildAttendeeWithInfosType>;
  all: ReadonlyArray<
    (AdultInitialAttendeesListType | ChildAttendeeWithInfosType) & {
      isChild?: boolean;
      isMain?: boolean;
      attendeeIndex: number;
      isAdult?: boolean;
      name?: string;
    }
  >;
};
type GetInitialFormValuesArgsType = {
  bookingProcess: ReadonlyArray<LegalInformation>;
  attendees: AttendeesType;
  connectedUserInfo: UserInfoType;
  hasAddress: boolean;
  locale: string;
  adultCivilityDefaultValue: string;
  childCivilityDefaultValue: string;
  getAttendeeSchema: (isMain: boolean, isChild: boolean) => Array<Record<string, ValidationSchema>>;
  displayParticipantsSelectInput: boolean;
  preCheckedOptins?: any;
};
type GetInitialFormValuesType = {
  adults: ReadonlyArray<
    AdultAttendeeWithInfosType & {
      birthdate: timestamp | null | undefined;
    }
  >;
  children: ReadonlyArray<
    AdultAttendeeWithInfosType & {
      birthdate: timestamp | null | undefined;
    }
  >;
};
export const verifyChildrenSiblings = (
  childBirthdate: (number | null | undefined) | (string | null | undefined),
  connectedUserInfo: any,
  siblingsIndexes: Array<number>,
) => {
  const childIndex = connectedUserInfo?.travelCompanions?.findIndex(
    ({ birthdate }, index) =>
      moment(childBirthdate).isSame(birthdate) && !siblingsIndexes.includes(index),
  );

  if (childIndex === undefined || childIndex === -1 || siblingsIndexes.includes(childIndex)) {
    return null;
  }

  siblingsIndexes.push(childIndex);
  return connectedUserInfo?.travelCompanions?.[childIndex];
};
export const getInitialFormValues = ({
  bookingProcess,
  adultCivilityDefaultValue,
  childCivilityDefaultValue,
  attendees,
  connectedUserInfo,
  hasAddress,
  locale,
  getAttendeeSchema,
  displayParticipantsSelectInput,
  preCheckedOptins = {},
}: GetInitialFormValuesArgsType): GetInitialFormValuesType => {
  const optins = getOptinsInfos(bookingProcess);

  // The opt-ins show are configurable from the CMS, but we also don't show them for identified
  // users who already checked them
  const getOptinValue = (optin, optinName, userInfo) => {
    if (!optin) {
      return;
    }

    // in case of a connected user return undefined to not show it if already checked
    if (connectedUserInfo[optinName] === true) {
      return undefined;
    }

    if (userInfo[optinName] !== undefined) {
      return userInfo[optinName];
    }

    return Boolean(preCheckedOptins[optinName] ?? optin.preChecked);
  };
  const defaultCountryCode = locale === 'en-IN' ? '' : locale.split('-')[1];

  const getDocumentInfo = (documents, documentType, type, attendeeSchema) => {
    const document = documents?.[documentType];

    const getFieldDefaultValue = (value, schemaFieldName, field) => {
      const defaultValue = {};

      // should only initialize these values if they exist in the schema V1
      if (attendeeSchema?.find((schema) => schema?.name === `${documentType}_${schemaFieldName}`)) {
        defaultValue[field] = value || defaultCountryCode;
      }

      return defaultValue;
    };

    const getFieldDefaulDate = (value, field) => {
      if (value && attendeeSchema?.find((schema) => schema?.name === `${documentType}_${field}`)) {
        return apiDateToTimestamp(value);
      }

      return value;
    };

    const issuingDate = getFieldDefaulDate(document?.issuingDate, 'issuing_date');
    const expirationDate = getFieldDefaulDate(document?.expirationDate, 'expiration_date');
    const nationality = getFieldDefaultValue(document?.nationality, 'nationality', 'nationality');
    const issuingCountry = getFieldDefaultValue(
      document?.issuingCountry,
      'issuing_country',
      'issuingCountry',
    );
    const birthCountry = getFieldDefaultValue(
      document?.birthCountry,
      'birth_country',
      'birthCountry',
    );
    const formattedDocument = {
      ...(document || {}),
      // initialises the 3 following fields only if they have no value & have rules in legalInfo
      ...nationality,
      ...issuingCountry,
      ...birthCountry,
      ...(issuingDate
        ? {
            issuingDate,
          }
        : {}),
      ...(expirationDate
        ? {
            expirationDate,
          }
        : {}),
    };
    // type is to be sent in Put Attendees should be in [PASSPORT, IDCARD]
    return { ...formattedDocument, type };
  };

  const getBirthday = (birthdate) =>
    birthdate && typeof birthdate === 'number' ? birthdate : apiDateToTimestamp(birthdate);

  const siblingsIndexes = [];
  const { adults, children } = attendees;
  return {
    adults: adults.map((adult, index) => ({
      ...adult,
      attendeeIndex: index,
      civility:
        displayParticipantsSelectInput && !connectedUserInfo?.isLoggedIn
          ? ''
          : adult.civility || adultCivilityDefaultValue,
      document: {
        passport: getDocumentInfo(
          adult.document,
          'passport',
          PASSPORT,
          getAttendeeSchema(!!adult.isMain, false),
        ),
        idcard: getDocumentInfo(
          adult.document,
          'idcard',
          IDCARD,
          getAttendeeSchema(!!adult.isMain, false),
        ),
      },
      birthdate: adult.birthdate && getBirthday(adult.birthdate),
      address:
        adult.isMain && hasAddress
          ? { ...adult.address, countryCode: adult.address?.countryCode || defaultCountryCode }
          : undefined,
      ...(adult.isMain
        ? {
            optinEmail: getOptinValue(optins.email, 'optinEmail', adult),
            optinSms: getOptinValue(optins.sms, 'optinSms', adult),
            optinMail: getOptinValue(optins.mail, 'optinMail', adult),
            optinFosun: getOptinValue(optins.fosun, 'optinFosun', adult),
            optinPhone: getOptinValue(optins.phone, 'optinPhone', adult),
            optinClubmedPlus: getOptinValue(optins.clubmedPlus, 'optinClubmedPlus', adult),
            optinsCGU: [],
            checkAll: false,
          }
        : null),
      mainContactId: !adult.gmNumber && adults[0].neolid ? adults[0].neolid : '-1', // temporarily disabled: waiting for clic evolv
    })),
    children: children.map((child, index) => {
      const childTravelCompanionData = verifyChildrenSiblings(
        child.birthdate,
        connectedUserInfo,
        siblingsIndexes,
      );
      const childWithInfos = childTravelCompanionData
        ? {
            birthdate: childTravelCompanionData?.birthdate,
            civility: childTravelCompanionData?.civility,
            document: childTravelCompanionData?.document,
            firstName: childTravelCompanionData?.firstName,
            lastName: childTravelCompanionData?.lastName,
            unicodeFirstName: childTravelCompanionData?.unicodeFirstName,
            unicodeLastName: childTravelCompanionData?.unicodeLastName,
          }
        : {};
      return {
        ...(child || {}),
        ...childWithInfos,
        attendeeIndex: index,
        civility: child?.civility || childCivilityDefaultValue,
        document: {
          passport: getDocumentInfo(
            child?.document,
            'passport',
            PASSPORT,
            getAttendeeSchema(false, true),
          ),
          idcard: getDocumentInfo(
            child?.document,
            'idcard',
            IDCARD,
            getAttendeeSchema(false, true),
          ),
        },
        birthdate: child.birthdate && getBirthday(child.birthdate),
        mainContactId: !child?.gmNumber && adults[0]?.neolid ? adults[0]?.neolid : '0',
      };
    }),
  };
};
export function getSchemaValidators(
  schema: Array<ValidationSchema>,
  {
    withRequired = true,
    requiredMessage = (fieldValidation: ValidationSchema) =>
      t(`booking.participants.errorMessage.${camelCase(fieldValidation.name)}`),
    messages = {},
    isDateType = false,
    isNullable = false,
  }: {
    withRequired?: boolean;
    requiredMessage?: (arg0: ValidationSchema) => string;
    messages?: Record<string, Record<string, string>>;
    isDateType?: boolean;
    isNullable?: boolean;
  } = {},
): any {
  return schema?.reduce((addressValidators, fieldValidation: ValidationSchema) => {
    let rule = isDateType ? yup.date() : yup.string();

    if (withRequired) {
      if (fieldValidation.required) {
        rule = rule.required(requiredMessage(fieldValidation));
      }
    }

    fieldValidation.validators.forEach((validator) => {
      const message = messages?.[fieldValidation.name]?.[validator.name];

      switch (validator.name) {
        case 'pattern':
          rule = rule.matches(
            new RegExp(validator.value),
            message || t('common.form.errors.invalidFieldFormat'),
          );
          break;

        case 'minLength':
          rule = rule
            .transform((value) => (value === '' ? undefined : value), message)
            .min(
              validator.value,
              message ||
                t('common.form.errors.invalidFieldTooShort', {
                  minlength: validator.value,
                }),
            );
          break;

        case 'maxLength':
          rule = rule.max(
            validator.value,
            message ||
              t('common.form.errors.invalidFieldTooLong', {
                maxlength: validator.value,
              }),
          );
          break;

        default:
          // eslint-disable-next-line no-console
          console.error(`Cannot handle rule ${validator.name}`);
      }
    });
    return { ...addressValidators, [fieldValidation.name]: isNullable ? rule.nullable() : rule };
  }, {});
}
type GetValidatorsArgsType = {
  attendeeSchema: Array<ValidationSchema>;
  addressSchema: Array<ValidationSchema>;
  locale: string;
  arrivalDate: any;
  adultCivilities: Array<string>;
  childCivilities: Array<string>;
};
type GetTravelDocumentsValidatorsArgsType = {
  attendeeSchema: Array<ValidationSchema>;
  locale: string;
  departureDate: any;
  arrivalDate: any;
};
export type ValidatorsType = {
  adultCivilities: () => boolean;
  childCivilities: () => boolean;
  firstName: () => boolean;
  lastName: () => boolean;
  unicodeFirstName: () => boolean;
  unicodeLastName: () => boolean;
  adultBirthDate: () => boolean;
  childBirthDate: () => boolean;
  email: () => boolean;
  mobile: () => boolean;
  healthPassId: () => boolean;
  emailNotRequired: () => boolean;
  mobileNotRequired: () => boolean;
  passportID: () => boolean;
  identityNumber: () => boolean;
  gmNumber: () => boolean;
  expirationDate: () => boolean;
  documentNumber: () => boolean;
  issuingDate: () => boolean;
  nationality: () => boolean;
  salesmanID: () => boolean;
  issuingCountry: () => boolean;
  birthCountry: () => boolean;
  birthCity: () => boolean;
  secondName: () => boolean;
  thirdName: () => boolean;
  fourthName: () => boolean;
  address: Record<string, () => boolean>;
};

const getDateSchemaByName = (attendeeSchema, validatorName) =>
  getSchemaValidators(
    attendeeSchema?.filter(({ name }) => name === validatorName),
    {
      isDateType: true,
    },
  ) || {};

const korean = /(\S*[\u3131-\u314e|\u314f-\u3163|\uac00-\ud7a3]+\S*)/;
const japanese = /(\S*[一-龠]+|[ぁ-ゔ]+|[ァ-ヴー]+|[a-zA-Z0-9]+|[ａ-ｚＡ-Ｚ０-９]+|[々〆〤ヶ]+\S*)/;
const hebrew = /^[\u0590-\u05fe]+$/;
const cyrillic = /(\S*[u0400-\u04FF]+$)/;
const chinese = /(\S*[\u4e00-\u9fff]+$)/;
const thai =
  /(\S*[\u0E01-\u0E5B]+|[\u1980-\u19DF]+|[\u1950-\u197F]+|[\u1A20-\u1AAF]+|[\uAA80-\uAADF]+$)/;
const asciiWordRegex = /^[\x20-\xFF]+$/; // ASCII

export const getNonAsciiNameValidatorByLocale = (locale: string) => {
  switch (locale) {
    case 'ko-KR':
      return korean;

    case 'zh-CN':
    case 'zh-HK':
    case 'zh-TW':
      return chinese;

    case 'ja-JP':
      return japanese;

    case 'ru-RU':
    case 'uk-UA':
    case 'sr-RS':
      return cyrillic;

    case 'he-IL':
      return hebrew;

    case 'th-TH':
      return thai;

    default:
      return asciiWordRegex;
  }
};
export const getValidators = ({
  attendeeSchema,
  addressSchema,
  locale,
  arrivalDate,
  adultCivilities,
  childCivilities,
}: GetValidatorsArgsType): ValidatorsType => {
  // Validators coming from API, to be merged with local validators
  const addressSchemaValidators =
    getSchemaValidators(addressSchema, {
      isNullable: true,
    }) || {};
  const attendeeSchemaValidators = getSchemaValidators(attendeeSchema) || {};
  const birthdateValidator = getDateSchemaByName(attendeeSchema, 'birthdate');
  yup.addMethod(yup.string, 'asciiNameValidator', function () {
    return this.matches(
      /^[^0-9!@#$%^&*()_+=\[\]{};:"\\|,<>\/?]+$/,
      t('common.form.errors.invalidFieldFormat'),
    );
  });
  yup.addMethod(yup.string, 'unicodeNameValidator', function () {
    return this.matches(
      getNonAsciiNameValidatorByLocale(locale),
      t('common.form.errors.invalidFieldFormat'),
    );
  });
  const phoneRegex = getPhoneRegex(locale);
  const rawValidators = {
    adultCivilities: yup
      .string()
      .oneOf(adultCivilities, t('booking.participants.errorMessage.civility'))
      .required(t('booking.participants.errorMessage.civility')),
    childCivilities: yup
      .string()
      .oneOf(childCivilities, t('booking.participants.errorMessage.gender'))
      .required(t('booking.participants.errorMessage.gender')),
    firstName: (attendeeSchemaValidators.first_name || yup.string())
      .asciiNameValidator()
      .matches(/^[\x20-\xFF]+$/, t('common.form.errors.invalidFieldFormat'))
      .max(20)
      .required(t('booking.participants.errorMessage.firstName')),
    lastName: (attendeeSchemaValidators.last_name || yup.string())
      .asciiNameValidator()
      .matches(/^[\x20-\xFF]+$/, t('common.form.errors.invalidFieldFormat'))
      .max(30)
      .required(t('booking.participants.errorMessage.lastName')),
    unicodeFirstName: (attendeeSchemaValidators.unicode_first_name || yup.string())
      .unicodeNameValidator()
      .matches(
        /^[^0-9!@#$%^&*()_+=\[\]{};:"\\|,<>\/?]+$/,
        t('common.form.errors.invalidFieldFormat'),
      )
      .max(100)
      .required(t('booking.participants.errorMessage.unicodeFirstName')),
    unicodeLastName: (attendeeSchemaValidators.unicode_last_name || yup.string())
      .unicodeNameValidator()
      .matches(
        /^[^0-9!@#$%^&*()_+=\[\]{};:"\\|,<>\/?]+$/,
        t('common.form.errors.invalidFieldFormat'),
      )
      .max(100)
      .required(t('booking.participants.errorMessage.unicodeLastName')),
    adultBirthDate: (birthdateValidator.birthdate || yup.date())
      .transform((_value, initialValue) => (initialValue ? new Date(initialValue) : initialValue))
      .max(moment(arrivalDate).subtract(YEAR_OF_MAJORITY, 'y'))
      .min(moment().subtract(MAX_AGE, 'y'))
      .required(t('booking.participants.errorMessage.birthdate')),
    childBirthDate: yup
      .date()
      .transform((_value, initialValue) => {
        return initialValue ? new Date(initialValue) : initialValue;
      })
      .max(moment())
      .min(moment().subtract(YEAR_OF_MAJORITY, 'y'))
      .required(t('booking.participants.errorMessage.birthdate')),
    email: (attendeeSchemaValidators.email || yup.string())
      .matches(emailRegex, t('common.form.errors.invalidFieldFormat'))
      .required(t('booking.participants.errorMessage.email')),
    mobile: yup
      .string()
      .matches(phoneRegex, t('common.form.errors.invalidFieldFormat'))
      .required(t('booking.participants.errorMessage.mobilePhone')),
    healthPassId: (attendeeSchemaValidators.health_pass_id || yup.string()).length(10),
    emailNotRequired: yup.string().email(),
    mobileNotRequired: yup
      .string()
      .transform((v) => (!v ? undefined : v)) // transform to undefined when empty string to pass the regex
      .matches(phoneRegex, t('common.form.errors.invalidFieldFormat')),
    gmNumber: (attendeeSchemaValidators.gm_number || yup.string()).required(),
  };
  return { ...rawValidators, address: addressSchemaValidators } as ValidatorsType;
};
export const getTravelDocumentsValidators = ({
  attendeeSchema,
  locale,
  departureDate,
  arrivalDate,
}: GetTravelDocumentsValidatorsArgsType): ValidatorsType => {
  // Validators coming from API, to be merged with local validators
  const attendeeSchemaValidators = getSchemaValidators(attendeeSchema) || {};
  const idcardExpDateValidator = getDateSchemaByName(attendeeSchema, 'idcard_expiration_date');
  const idcardIssuingDateValidator = getDateSchemaByName(attendeeSchema, 'idcard_issuing_date');
  const passportExpDateValidator = getDateSchemaByName(attendeeSchema, 'passport_expiration_date');
  const passportIssDateValidator = getDateSchemaByName(attendeeSchema, 'passport_issuing_date');
  const isPassportNumberRequired = !!attendeeSchema?.find(
    ({ name }) => name === 'passport_document_number',
  )?.required;
  const isIdCardNumberRequired = !!attendeeSchema?.find(
    ({ name }) => name === 'idcard_document_number',
  )?.required;
  const identityNumberGeneralRule = identityNumberValidatorReducer(locale, attendeeSchema);
  yup.addMethod(yup.string, 'passportNumberRule', function () {
    return this.test(
      'is-passport-required',
      t('booking.participants.errorMessage.identityNumber'),
      (value) => {
        const trimmedDocNumber = value?.trim();

        if (trimmedDocNumber) {
          // typed somthing apart from white spaces
          const isValidDocNumber = Boolean(trimmedDocNumber?.match(/[a-zA-Z0-9]{6,}$/));
          return isValidDocNumber;
        }

        return !isPassportNumberRequired;
      },
    ).max(30);
  });
  yup.addMethod(yup.string, 'idcardNumberRule', function () {
    return this.test(
      'is-idcard-required',
      t('booking.participants.errorMessage.identityNumber'),
      (value) => {
        const trimmedDocNumber = value?.trim();

        if (trimmedDocNumber) {
          // typed somthing apart from white spaces
          const isValidDocNumber = Boolean(trimmedDocNumber?.match(/[a-zA-Z0-9]{6,}$/));
          return isValidDocNumber;
        }

        return !isIdCardNumberRequired;
      },
    ).max(30);
  });
  yup.addMethod(yup.date, 'expirationDateRule', function () {
    return this.transform((_value, initialValue: string | Date | null | undefined) => {
      return initialValue ? new Date(moment(initialValue).toDate()) : initialValue;
    }).min(moment(departureDate).add(1, 'd'));
  });
  yup.addMethod(yup.date, 'issuingDateRule', function () {
    return this.transform((_value, initialValue: string | Date | null | undefined) => {
      return initialValue ? new Date(moment(initialValue).toDate()) : initialValue;
    }).max(moment(arrivalDate).subtract(1, 'd'));
  });
  const rawValidators = {
    idcardDocumentNumber: (attendeeSchemaValidators.idcard_document_number || yup.string())
      .nullable()
      .idcardNumberRule(),
    passportDocumentNumber: (attendeeSchemaValidators.passport_document_number || yup.string())
      .nullable()
      .passportNumberRule(),
    idcardExpirationDate: (idcardExpDateValidator.idcard_expiration_date || yup.date())
      .nullable()
      .expirationDateRule(),
    passportExpirationDate: (passportExpDateValidator.passport_expiration_date || yup.date())
      .nullable()
      .expirationDateRule(),
    idcardIssuingDate: (idcardIssuingDateValidator.idcard_issuing_date || yup.date())
      .nullable()
      .issuingDateRule(),
    passportIssuingDate: (passportIssDateValidator.passport_issuing_date || yup.date())
      .nullable()
      .issuingDateRule(),
    idcardNationality: (attendeeSchemaValidators.idcard_nationality || yup.string()).nullable(),
    passportNationality: (attendeeSchemaValidators.passport_nationality || yup.string()).nullable(),
    idcardAuthority: (attendeeSchemaValidators.idcard_authority || yup.string()).nullable(),
    passportAuthority: (attendeeSchemaValidators.passport_authority || yup.string()).nullable(),
    idcardBirthCountry: (attendeeSchemaValidators.idcard_birth_country || yup.string()).nullable(),
    idcardBirthCity: (attendeeSchemaValidators.idcard_birth_city || yup.string()).nullable(),
    passportBirthCountry: attendeeSchemaValidators.passport_birth_country || yup.string(),
    passportBirthCity: (attendeeSchemaValidators.passport_birth_city || yup.string()).nullable(),
    identityNumber: attendeeSchemaValidators.identity_number || identityNumberGeneralRule,
  };
  return rawValidators as ValidatorsType;
};
export const getLegalAgreementValidator = () => {
  return yup.mixed().oneOf([true]).required();
};
export const getLegalAgreementValidatorMulti = (requiredIds: string[]) => {
  return yup.object().shape(
    Object.fromEntries(
      requiredIds.map((id) => [
        id,
        yup.object({
          optin: yup.boolean().oneOf([true]).required(),
          optin_partners: yup.boolean(),
          channel: yup.string(),
        }),
      ]),
    ),
  );
};
export const getSalesmanIdValidator = ({
  attendeeSchema,
}: {
  attendeeSchema: Array<ValidationSchema>;
}) => {
  const attendeeSchemaValidators = getSchemaValidators(attendeeSchema);
  return (attendeeSchemaValidators?.salesman_id || yup.string()).matches(salesmanIDRegex);
};

function getDocumentsFields(bookingProcess: ReadonlyArray<LegalInformation>): DocumentsByTypes {
  return DOCUMENT_TYPES.reduce((fields, type) => {
    const filteredFields = {
      type: type.toLowerCase(),
      values: bookingProcess.filter((field) => field.type === type),
    };
    // returns all fields in bookingProcess or TransportBookingProcess with the current type(s)
    filteredFields.values.length > 0 && fields.push(filteredFields);
    return fields;
  }, []);
}

export const getAdultCivilities = (defaultCivilities: any, locale: string) => {
  const adultConfigCivilities = getAdultsCivilities(locale) || [];
  return isEmpty(defaultCivilities) ? adultConfigCivilities : defaultCivilities;
};
export const getChildCivilities = (defaultCivilities: any, locale: string) => {
  const childConfigCivilities = getKidsCivilities(locale) || [];
  return isEmpty(defaultCivilities) ? childConfigCivilities : defaultCivilities;
};
type GetHeaderContactInfoTitleArgsType = {
  isMain: boolean;
  isChild: boolean;
  hasUnicodeFields: boolean;
  formValues: {};
  attendeeIdentifier: string;
  attendeeIndex: number;
  isValid: boolean;
};
export const getHeaderContactInfoTitle = ({
  isMain,
  isChild,
  hasUnicodeFields,
  formValues,
  attendeeIdentifier,
  attendeeIndex,
  isValid,
}: GetHeaderContactInfoTitleArgsType) => {
  if (hasUnicodeFields && isValid) {
    return `${get(formValues, `${attendeeIdentifier}.unicodeFirstName`)} ${get(formValues, `${attendeeIdentifier}.unicodeLastName`)}`;
  }

  const firstName = get(formValues, `${attendeeIdentifier}.firstName`);
  const lastName = get(formValues, `${attendeeIdentifier}.lastName`);

  if (!!firstName && !!lastName) {
    return `${firstName} ${lastName}`;
  }

  if (isMain) {
    return t('booking.participants.personnalInfosHeader.title');
  }

  if (isChild) {
    return `${t('booking.participants.otherKidsInfosHeader.title')} ${attendeeIndex + 1}`;
  }

  return `${t('booking.participants.otherAdultsInfosHeader.title')} ${attendeeIndex}`;
};

const shouldHideField = ({
  formInitialValues,
  connectedUserInfo,
  attendeeIdentifier,
  fieldName,
  isMain,
  // like for the phone value sometimes the name of a field is not the same between contact
  // form and the user store
  fieldNameConnectedUserOverride,
  isChild = false,
}: {
  connectedUserInfo?: UserInfoType;
  fieldName: string;
  fieldNameConnectedUserOverride?: string;
  isChild?: boolean;
  isMain?: boolean;
  formInitialValues?: {};
  attendeeIdentifier?: string;
}) => {
  const siblingsIndexes = [];
  let childTravelCompanionData = null;

  if (attendeeIdentifier) {
    const childAttendeeFirstName = get(formInitialValues, `${attendeeIdentifier}.firstName`);

    if (!childAttendeeFirstName) {
      verifyChildrenSiblings(
        timestampToApiDate(get(formInitialValues, `${attendeeIdentifier}.birthdate`)),
        connectedUserInfo,
        siblingsIndexes,
      );
    }

    childTravelCompanionData =
      connectedUserInfo?.travelCompanions &&
      verifyChildrenSiblings(
        timestampToApiDate(get(formInitialValues, `${attendeeIdentifier}.birthdate`)),
        connectedUserInfo,
        siblingsIndexes,
      );
  }

  return (
    (connectedUserInfo &&
      !isChild &&
      isMain &&
      ((fieldNameConnectedUserOverride && connectedUserInfo[fieldNameConnectedUserOverride]) ||
        connectedUserInfo[fieldName])) ||
    (isChild && connectedUserInfo && childTravelCompanionData?.[fieldName])
  );
};

export const findDuplicateAttendees = (
  attendees: ReadonlyArray<{
    birthdate: number;
    firstName: string;
    lastName: string;
  }>,
) => {
  const duplicates = attendees?.reduce((acc, attendee, index) => {
    if (attendee) {
      const { birthdate, lastName, firstName } = attendee;

      if (birthdate && lastName && firstName) {
        const key = `${birthdate}${lastName}${firstName}`.toLocaleLowerCase();

        if (acc[key]) {
          acc[key].push(index);
        } else {
          acc[key] = [index];
        }
      }
    }

    return acc;
  }, {});
  Object.keys(duplicates)?.forEach((key) => {
    if (duplicates?.[key]?.length < 2) {
      delete duplicates[key];
    }
  });
  return flatten(Object.values(duplicates));
};

const generateErrorFieldsObj = (erroredFieldsArray) => {
  // Initialize the result object with 'no-error' for each field
  const result = {};

  for (let i = 0; i < Object.keys(FORM_ERROR_OBJECT).length; i++) {
    result[i] = 'no-error';
  }

  // Update the result for selected fields based on the input array
  erroredFieldsArray.forEach((field) => {
    const key = Object.keys(FORM_ERROR_OBJECT).find((k) => FORM_ERROR_OBJECT[k] === field);

    if (key !== undefined) {
      result[key] = field;
    }
  });
  return [result];
};

export const trackAttendeesFormError = (
  trackEvent: (name: string, payload: TrackPayload) => void,
  formik: Formik,
): void => {
  const getFields = (errors) =>
    Object.entries(errors || {})
      .reduce((acc, [key, value]) => {
        if (isObject(value)) {
          return [...acc, ...getFields(value)];
        }

        return [...acc, key];
      }, [])
      .sort();

  const trackFormErrors = (formName, errors) => {
    trackEvent(TRACK_EVENT_NAMES.FORM_ERROR, {
      error_status: {
        form_name: formName,
        fields: generateErrorFieldsObj(getFields(errors)),
      },
    });
  };

  const trackForms = (prefix, forms = []) => {
    forms.forEach((errors, i) => {
      trackFormErrors(`${prefix}_${i + 1}`, errors);
    });
  };

  trackForms('adult', formik.errors?.adults);
  trackForms('child', formik.errors?.children);
};

const parseBirthdate = (birthdate: string | number): number => {
  if (typeof birthdate === 'string') {
    return moment(birthdate).valueOf();
  }

  return birthdate;
};

export const isChildWillBeAdultByArrivalDate = (
  childBirthdate: number | string,
  arrivalDate: string,
): boolean => {
  const childBirthdateValue = parseBirthdate(childBirthdate);
  const childAge = moment().diff(childBirthdateValue, 'years');
  const childAgeByArrivalDate = moment(arrivalDate).diff(childBirthdateValue, 'years');
  return childAge < 18 && childAgeByArrivalDate >= 18;
};
export type OtherAdultAttendeeWithInfosType = Readonly<{
  id: string;
  neolid?: string;
  civility?: string;
  firstName?: string;
  lastName?: string;
  unicodeFirstName?: string;
  unicodeLastName?: string;
  email?: string;
  mobile?: string;
  birthdate?: number;
  gmNumber: string | null | undefined;
}>;
export const tripInformationToAttendeesList = (tripInformation: TripInformation): any => {
  const attendeesList = [
    ...tripInformation.adults.map((adult, index) => ({ ...adult, isMain: index === 0 })),
    ...(tripInformation.kids?.map((child) => ({ ...child, isChild: true })) || []),
  ];
  return {
    adults: attendeesList.filter(({ isChild }) => !isChild),
    children: attendeesList.filter(({ isChild }) => isChild),
    all: attendeesList.map((attendee, index) => ({ ...attendee, attendeeIndex: Number(index) })),
  };
};
export const travelCompanionMapper = ({
  companionId,
  civility,
  firstName,
  lastName,
  birthdate,
  document,
  mobile,
  email,
  customerId,
  mainContactId,
  isSelected,
  isChild,
  unicodeFirstName,
  unicodeLastName,
  identityNumber,
}: TravelCompanion) => ({
  companionId,
  civility,
  firstName,
  lastName,
  document,
  mobile,
  email,
  customerId,
  mainContactId,
  unicodeFirstName,
  unicodeLastName,
  identityNumber,
  isSelected: isSelected || false,
  ...((isChild ||
    attendeesUtils.isKid({
      birthdate,
    })) && {
    isChild: true,
  }),
  ...(birthdate && {
    birthdate: parseBirthdate(birthdate),
  }),
});
export const getNextCharacter = (char: string) => {
  return String.fromCharCode(char.charCodeAt(0) + 1);
};
export const createId = (index: number, char: string) => {
  return String.fromCharCode(char.charCodeAt(0) + index);
};
export type customAttendeesListType = {
  adultsList: ReadonlyArray<AdultInitialAttendeesListType | OtherAdultAttendeeWithInfosType>;
  childrenList: ReadonlyArray<ChildAttendeeWithInfosType>;
  allList: ReadonlyArray<
    | (AdultInitialAttendeesListType & {
        isChild?: boolean;
        isMain?: boolean;
        attendeeIndex: number;
      })
    | (ChildAttendeeWithInfosType & {
        isChild?: boolean;
        isMain?: boolean;
        attendeeIndex: number;
      })
  >;
};
export const getChildrenToAdultsTransferList = (
  attendeesList: InitialAttendeesListType,
  arrivalDate: string,
): any => {
  const { adults, children } = attendeesList;

  const toBeAdultFilter = ({ birthdate }) =>
    birthdate && isChildWillBeAdultByArrivalDate(birthdate, arrivalDate);

  const indexMap = (attendee, index) => ({ ...attendee, attendeeIndex: Number(index) });

  const adultMapper = ({ id, civility, firstName, lastName, birthdate, neolid, gmNumber }) => ({
    id,
    civility,
    firstName,
    lastName,
    birthdate,
    neolid,
    gmNumber,
    isMain: false,
  });

  // Collect existing IDs to avoid duplicates
  const existingIds = new Set();
  // First pass: collect all existing IDs
  adults.forEach((adult) => {
    if (adult.id) {
      existingIds.add(adult.id);
    }
  });
  children.forEach((child) => {
    if (child.id) {
      existingIds.add(child.id);
    }
  });

  const getNextAvailableId = () => {
    let currentChar = 'A';

    for (let i = 0; existingIds.has(currentChar) && i < 26; i++) {
      currentChar = getNextCharacter(currentChar);
    }

    existingIds.add(currentChar);
    return currentChar;
  };

  const adultsList = [...adults, ...children.filter(toBeAdultFilter).map(adultMapper)].map(
    (el) => ({ ...el, id: el.id || getNextAvailableId() }),
  );
  const childrenList = children
    .filter((child) => !children.filter(toBeAdultFilter).includes(child))
    .map((el) => ({ ...el, id: el.id || getNextAvailableId() }));
  return {
    adultsList,
    childrenList,
    allList: [...adultsList.map(indexMap), ...childrenList.map(indexMap)],
  };
};
export const compareById = <
  T extends {
    id: string;
  },
>(
  a: T,
  b: T,
) => a.id.localeCompare(b.id);
export const compareByBirthdate = (a: any, b: any): number =>
  moment(a.birthdate).valueOf() - moment(b.birthdate).valueOf();
export const getSortedTravelCompanionList = (
  travelCompanionList: Array<TravelCompanion> = [],
): Array<TravelCompanion> => {
  const travelCompanionListWithoutBirthdate = travelCompanionList
    .filter(({ birthdate }) => !birthdate)
    .map(({ birthdate, ...rest }) => ({ ...rest }));
  const sortedTravelCompanionList = travelCompanionList
    .filter(({ birthdate }) => birthdate)
    .sort(compareByBirthdate);
  return [...travelCompanionListWithoutBirthdate, ...sortedTravelCompanionList]
    .filter(({ isMain }) => !isMain)
    .map((travelCompanion, index) => ({ ...travelCompanion, companionId: index }))
    .map(travelCompanionMapper);
};
export const filterSearchedChildren = (
  travelCompanionList: Array<TravelCompanion> = [],
  allAttendees: any,
) => {
  const sortedCompanionsList = getSortedTravelCompanionList(travelCompanionList);
  const travelCompanionAdultList = sortedCompanionsList.filter(({ isChild }) => !isChild);
  const travelCompanionChildrenList = sortedCompanionsList.filter(({ isChild }) => isChild);
  const formattedCompanionChildrenList = uniqBy(travelCompanionChildrenList, 'birthdate')?.map(
    (child) => {
      return { ...child, birthdate: child.birthdate, isChild: true, isMain: false };
    },
  );
  const selectedCompanionChildrenList = formattedCompanionChildrenList.filter(
    ({ isSelected }) => isSelected,
  );
  const firstChildIndex = allAttendees?.adults?.length;
  const selectedChildrenIndexes = selectedCompanionChildrenList.map<any>((_, i: number) => ({
    index: firstChildIndex + i,
    position: i,
  }));
  const finalCompanionsList = [...travelCompanionAdultList, ...selectedCompanionChildrenList];
  return {
    travelCompanions: finalCompanionsList,
    selectedIndexes: selectedChildrenIndexes,
  };
};
type GetFieldErrorsArgs = {
  errors: any;
  index: number;
  isChild: boolean;
};
export const getFieldErrors = ({ errors, index, isChild }: GetFieldErrorsArgs) => {
  return isChild ? errors?.children?.[index] : errors?.adults?.[index];
};

const isLetter = (char: string) => !!(char?.length === 1 && char?.match(/[a-z]/i));

export const findAlphabetIndex = (char: string) => {
  const base = 'a'.charCodeAt(0);
  return isLetter(char) ? char.toLowerCase().charCodeAt(0) - base : 0;
};
type SetTravelCompanionBlockFormErrorsArgs = {
  errors: any;
  isChild: boolean;
  travelCompanionIndex: number;
  setFieldTouched: (arg0: any, arg1: any) => void;
};
export const setTravelCompanionBlockFormErrors = ({
  errors,
  isChild,
  travelCompanionIndex,
  setFieldTouched,
}: SetTravelCompanionBlockFormErrorsArgs) => {
  const fieldErrors = isChild
    ? errors?.children?.[travelCompanionIndex]
    : errors?.adults?.[travelCompanionIndex];
  const fieldErrorNames = fieldErrors ? Object.keys(fieldErrors) : [];
  fieldErrorNames.forEach((fieldErrorName) => {
    setFieldTouched(
      `${isChild ? 'children' : 'adults'}[${travelCompanionIndex}].${fieldErrorName}`,
      true,
    );
  });
};
type GetTravelCompanionInfosArgs = {
  travelCompanionSelector: {
    list: Array<TravelCompanion>;
    selectedIndexes: Array<any>;
  };
  isChild: boolean;
  parentIndex: number;
};
export const getTravelCompanionInfos = ({
  travelCompanionSelector,
  isChild,
  parentIndex,
}: GetTravelCompanionInfosArgs) => {
  const travelCompanionAdultList = travelCompanionSelector.list.filter(
    (travelCompanion) => !travelCompanion.isChild,
  );
  const travelCompanionChildrenList = travelCompanionSelector.list.filter(
    (travelCompanion) => travelCompanion.isChild,
  );
  const travelCompanionList = isChild ? travelCompanionChildrenList : travelCompanionAdultList;
  const hasSelectedTravelCompanion = !!travelCompanionList.find(({ isSelected }) => isSelected);
  const hasParentIndexInSelectedIndexes = travelCompanionSelector.selectedIndexes.some(
    (indexes) => indexes.index === parentIndex,
  );
  return {
    travelCompanionList,
    hasParentIndexInSelectedIndexes,
    isDisabled: hasSelectedTravelCompanion && hasParentIndexInSelectedIndexes,
  };
};
type SetTravelCompanionFieldArgs = {
  travelCompanion: any;
  isChild: boolean;
  parentIndex: number;
  values: any;
  setFieldValue: (arg0: any, arg1: any) => void;
  attendeeIdentifier: string;
};
export const setTravelCompanionField = ({
  travelCompanion,
  isChild,
  parentIndex,
  values,
  setFieldValue,
  attendeeIdentifier,
}: SetTravelCompanionFieldArgs): any => {
  // Collect all existing IDs to avoid duplicates, excluding the current participant being edited
  const existingIds = new Set();
  values.adults?.forEach((adult, index) => {
    // Exclude the current participant if it's an adult
    if (adult.id && !(index === parentIndex && !isChild)) {
      existingIds.add(adult.id);
    }
  });
  values.children?.forEach((child, index) => {
    // Exclude the current participant if it's a child
    if (child.id && !(index === parentIndex && isChild)) {
      existingIds.add(child.id);
    }
  });
  // Find the first available ID starting from 'A'
  let newId = 'A';

  for (let i = 0; existingIds.has(newId) && i < 26; i++) {
    newId = getNextCharacter(newId);
  }

  const fieldObject = {
    ...travelCompanion,
    // Here we create the actual id that will be in sync with number of fields we have in the form
    id: newId,
    mainContactId: values.adults[0].mainContactId,
    birthdate: moment(travelCompanion?.birthdate),
    customerId: '',
    attendeeIndex: parentIndex,
    ...(!isChild && {
      isMain: false,
    }),
  };
  return setFieldValue(attendeeIdentifier, fieldObject);
};

const getHouseholdsAttendees = (
  householdsList: Array<MemberHousehold>,
): Array<HouseholdAttendee> => {
  let tempAttendees = [];
  householdsList.forEach(({ adults, children }) => {
    const mainAdultsInCurrentHousehold = adults.filter((attendee) => attendee?.isMain).length;
    let newAdults = [...adults];

    // if the Main adult was selected else where; redefine a new main adult in the current household
    if (adults.length && mainAdultsInCurrentHousehold === 0) {
      newAdults = newAdults.map((adult, index) => ({ ...adult, isMain: index === 0 }));
    }

    tempAttendees = [...tempAttendees, ...newAdults, ...children];
  });
  return tempAttendees.sort((a, b) => {
    if (a.id < b.id) {
      return -1;
    }

    if (a.id > b.id) {
      return 1;
    }

    return 0;
  });
};

const setErrorsForConcernedAttendees = ({
  concernedAttendees,
  proposalAttendees,
  errors,
  fieldsOnError,
}: {
  concernedAttendees: Array<string>;
  proposalAttendees: ProposalAttendees;
  errors: any;
  fieldsOnError: FieldsOnErrorType;
}) => {
  const updatedErrors = { ...errors };
  proposalAttendees.adults?.forEach((adult, index) => {
    if (concernedAttendees.includes(adult.id)) {
      updatedErrors.adults[index] = { ...updatedErrors.adults[index], ...fieldsOnError };
    }
  });
  proposalAttendees.children?.forEach((child, index) => {
    if (concernedAttendees.includes(child.id)) {
      updatedErrors.children[index] = { ...updatedErrors.children[index], ...fieldsOnError };
    }
  });
  return updatedErrors;
};

const handleSetErrorsForConcernedAttendees = ({
  concernedAttendees,
  proposalAttendees,
  setErrors,
}: {
  concernedAttendees: Array<string>;
  proposalAttendees: ProposalAttendees;
  setErrors: any;
}) => {
  if (concernedAttendees?.length > 0) {
    const fieldsOnError = {
      document: {
        passport: {
          expirationDate: t('booking.participants.errorMessage.invalidPassportExpirationDate'),
        },
      },
    };
    const errors = {
      adults: Array(proposalAttendees.adults?.length).fill(null),
      children: Array(proposalAttendees.children?.length || 0).fill(null),
    };
    const updatedErrors = setErrorsForConcernedAttendees({
      concernedAttendees,
      proposalAttendees,
      errors,
      fieldsOnError,
    });
    setErrors(updatedErrors);
  }
};

export {
  getCNILLegalMessage,
  getConcernedAttendees,
  getLegalInformation,
  submitOption,
  submitOptionFromProposal,
  getLegalInputInformation,
  shouldDisplayLegalInformation,
  handleSetErrorsForConcernedAttendees,
  renderErrorUpdatingAttendees,
  getAllAttendeesIds,
  shouldHideField,
  getDocumentsFields,
  getHouseholdsAttendees,
};
